/**
 * Utility functions for PagesContainer components
 * Functions for extracting and processing markup/XML children nodes
 */

import { isSet, getWindowDataUsingId, conditionRulesChecking } from "../../Utils/CommonFunctions";
import * as ShipConstants from '../../Global/Constants';
import { getPublicDomainName, getWindowObjectUsingWindowId } from '../../Utils/UserFunctions';

const LOOKUP_RECORD_ID_FIELD = "record_id";

export const getPagesConfigExecuteUrl = (automationPageDetails) => {
  const params = new URLSearchParams(window.location.search);
  const isPublic = params.get('public') === 'true';
  if (isPublic && automationPageDetails?.configuration_api_name) {
    const domainName = getPublicDomainName();
    return `public-pages/${domainName}/${automationPageDetails.configuration_api_name}/execute`;
  }
  if (!automationPageDetails?.id) {
    return null;
  }
  return `pageconfig/${automationPageDetails.id}/pages-config-execute`;
};

const parseFilterVarFields = (filterVar = "") => {
  if (!filterVar || typeof filterVar !== "string") return [];
  return filterVar.split(",").map((s) => s.trim()).filter(Boolean);
};

export const getFilterPayloadValue = (fieldFormPayload, fieldName) => {
  if (!fieldName || !fieldFormPayload) return undefined;
  const entry = fieldFormPayload[fieldName];
  if (entry == null) return undefined;
  if (typeof entry === "object" && "selected_value" in entry) {
    return entry.selected_value;
  }
  return entry;
};

const formatExecuteSelectedValue = (selectedValue) => {
  if (Array.isArray(selectedValue)) return selectedValue;
  if (selectedValue && typeof selectedValue === "object") return [selectedValue];
  return selectedValue;
};

const ARRAY_OBJECT_FIELD_TYPES = ["Array", "Object"];
const LOOKUP_FIELD_TYPES = ["Lookup", "MultiLookup"];

export const isArrayOrObjectPagesVariable = (variableMeta) => {
  const fieldType = variableMeta?.fieldtype_name ?? variableMeta?.type;
  return ARRAY_OBJECT_FIELD_TYPES.includes(fieldType);
};

export const isLookupPagesVariable = (variableMeta) => {
  if (!variableMeta) return false;
  const fieldType = variableMeta?.fieldtype_name ?? variableMeta?.type;
  if (LOOKUP_FIELD_TYPES.includes(fieldType)) return true;
  return isSet(variableMeta.window_field_id_path, null) !== null;
};

const parseAccidentalJsonString = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return value;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return value;
  }
};

/**
 * Build selected_value for pages-config-execute.
 * STEPS/API: native JSON values (Steps serializes server-side).
 * RATINGS: JSON-stringify only Array/Object variables (HyperThread .toString() workaround).
 * Lookup/MultiLookup/window-field vars: never stringify (by variable metadata, not value shape).
 */
export const formatPagesExecuteSelectedValue = (
  selectedValue,
  variableMeta,
  automationType
) => {
  const finalValue = formatExecuteSelectedValue(
    parseAccidentalJsonString(selectedValue)
  );

  if (
    automationType === "RATINGS" &&
    isArrayOrObjectPagesVariable(variableMeta) &&
    !isLookupPagesVariable(variableMeta) &&
    finalValue != null &&
    finalValue !== ""
  ) {
    return JSON.stringify(
      Array.isArray(finalValue) ? finalValue : [finalValue]
    );
  }

  return finalValue;
};

export const resolvePagesVariableMeta = (resourcesList, item, apiName) => {
  const pool = [
    ...(resourcesList?.input || []),
    ...(resourcesList?.variables || []),
  ];
  return (
    pool.find((v) => v.id === item.pages_variable_id) ||
    (apiName &&
      (pool.find((v) => v.api_name === apiName) ||
        pool.find((v) => v.variable_name === apiName))) ||
    null
  );
};

/** Shared pages-config-execute payload builder (button submit + Redux auto-execute). */
export const buildPagesConfigExecutePayload = ({
  fromMapping,
  resourcesList,
  automationType,
  formPayload = {},
  params = {},
}) => {
  const inputMap = Object.fromEntries(
    (resourcesList?.input || []).map((item) => [item.id, item.api_name])
  );

  return (fromMapping || []).map((item) => {
    const apiName = isSet(item?.pages_variable_name, inputMap[item.pages_variable_id]);
    let selectedValue;

    if (apiName) {
      const lowerKey = apiName.toLowerCase();
      if (
        item.pages_variable_name &&
        Object.prototype.hasOwnProperty.call(params, item.pages_variable_name)
      ) {
        const recordIds = params[item.pages_variable_name];
        selectedValue = isSet(recordIds, "")
          .split(",")
          .map((id) => ({ id: id.trim() }));
      } else if (Object.prototype.hasOwnProperty.call(params, lowerKey)) {
        selectedValue = params[lowerKey];
      }  else if (
        item.selected_value !== undefined &&
        item.selected_value !== null &&
        item.selected_value !== ""
      ) {
        selectedValue = item.selected_value;
      } else {
        selectedValue = formPayload[apiName]?.selected_value;
      }
    }

    const variableMeta = resolvePagesVariableMeta(resourcesList, item, apiName);
    const finalValue = formatPagesExecuteSelectedValue(
      selectedValue,
      variableMeta,
      automationType
    );

    return {
      ...item,
      selected_value: isSet(finalValue, ""),
    };
  });
};

/**
 * Resolve post-automation redirect URL from page configuration redirect fields.
 * Mirrors Action Button redirect logic in LayoutForms / DetailView.
 */
export const resolveRedirectUrl = (redirectConfig, variables = []) => {
  if (!redirectConfig) return null;
  const {
    redirect_type,
    redirect_window_id,
    redirect_data_id,
    redirect_url_expression,
  } = redirectConfig;

  if (!isSet(redirect_type, '')) return null;

  if (redirect_type === 'OTHER') {
    const customUrl = isSet(redirect_url_expression, null);
    return customUrl !== null && customUrl !== '' ? customUrl : null;
  }

  if (!ShipConstants.REDIRECT_URLS[redirect_type]) return null;

  let dataId = '';
  (variables || []).forEach((variable) => {
    if (variable.id === redirect_data_id) {
      dataId = variable.value;
    }
  });
  if (dataId != null && dataId !== undefined) {
    dataId = String(dataId).replace(/"/g, '');
  }

  const windowNameObj = getWindowDataUsingId(redirect_window_id);
  const windowObj = getWindowObjectUsingWindowId(redirect_window_id);
  if (!windowNameObj || !windowObj) return null;

  let url = ShipConstants.REDIRECT_URLS[redirect_type];
  url = url.replace('${dataId}', dataId);
  url = url.replace('${windowName}', windowObj.window_name);
  url = url.replace('${windowId}', windowNameObj.window_id);
  url = url.replace('${layoutId}', windowNameObj.layout_id);

  if (redirect_type === 'DETAIL' && isSet(dataId, null) === null) {
    return null;
  }
  return url;
};

/** Navigate via history when redirect URL resolves; returns true if navigation occurred. */
export const resolveAndNavigateRedirect = ({
  redirectConfig,
  variables,
  history,
  onBeforeNavigate,
}) => {
  const url = resolveRedirectUrl(redirectConfig, variables);
  if (!url || !history || typeof history.push !== 'function') return false;
  if (typeof onBeforeNavigate === 'function') {
    onBeforeNavigate();
  }
  history.push(url);
  return true;
};

export const emptyToPageRedirect = () => ({
  redirect_type: '',
  redirect_window_id: '',
  redirect_data_id: '',
  redirect_url_expression: '',
});

const REDIRECT_TYPE_LABELS = {
  EDIT: 'Edit Page',
  DETAIL: 'Detail Page',
  LIST: 'List Page',
};

/** Human-readable redirect target for rule description and redirect field summary. */
export const getRedirectTargetSummary = (
  redirect,
  { windowListOptions = [], outputVariables = [], includeTypeLabel = true } = {}
) => {
  const redirectType = isSet(redirect?.redirect_type, '');
  if (!redirectType) {
    return '';
  }
  if (redirectType === 'OTHER') {
    return isSet(redirect?.redirect_url_expression, '').trim() || 'Custom URL';
  }
  const windowOpt = isSet(windowListOptions, []).find(
    (item) => item.value === redirect?.redirect_window_id
  );
  const varOpt = isSet(outputVariables, []).find(
    (item) => item.id === redirect?.redirect_data_id
  );
  const parts = [];
  if (includeTypeLabel && REDIRECT_TYPE_LABELS[redirectType]) {
    parts.push(REDIRECT_TYPE_LABELS[redirectType]);
  }
  if (windowOpt) {
    parts.push(windowOpt.label);
  }
  if (varOpt && (redirectType === 'EDIT' || redirectType === 'DETAIL')) {
    parts.push(varOpt.variable_name || varOpt.api_name);
  }
  return parts.join(' → ');
};

export const isToPageRedirectMode = (toPage) => {
  if (toPage?.to_page_action_type === 'PAGE') {
    return false;
  }
  if (toPage?.to_page_action_type === 'REDIRECT') {
    return true;
  }
  return toPage?.redirect != null;
};

export const isPlaceholderRulePageId = (pageId) =>
  isSet(pageId, '').startsWith('__rule_');

/** Page ids already used by other open-page rules (excludes redirect rules and placeholders). */
export const getUsedOpenPageIdsForRules = (toPages = [], excludeIndex) =>
  isSet(toPages, [])
    .filter((rule, idx) => idx !== excludeIndex && !isToPageRedirectMode(rule))
    .map((rule) => isSet(rule.page_id, '').trim())
    .filter((id) => id && id !== 'SAME_PAGE' && !isPlaceholderRulePageId(id));

const stripUiOnlyToPageFields = (entry = {}) => {
  const { to_page_action_type, ...clean } = entry;
  return clean;
};

/** Normalize one to_page_mapping entry to nested redirect shape. */
export const normalizeToPageEntry = (entry = {}) => {
  const field_mappings = isSet(entry?.field_mappings, []);
  const rawCondition = isSet(entry?.condition, []);
  const isRedirect = isToPageRedirectMode(entry);
  const condition = isRedirect
    ? rawCondition
    : rawCondition.map((item) => ({
        ...item,
        redirect_mapping_id: null,
      }));
  if (isRedirect && entry.redirect != null && typeof entry.redirect === 'object') {
    return stripUiOnlyToPageFields({ ...entry, field_mappings, condition });
  }
  return stripUiOnlyToPageFields({ ...entry, field_mappings, condition, redirect: null });
};

/** Normalize full to_page_mapping list to nested redirect shape. */
export const normalizeToPageMappingList = (data) =>
  isSet(data?.to_page_mapping, []).map(normalizeToPageEntry);

/** Build outputResponse map from automation variables (api_name -> variable). */
export const buildOutputResponseFromVariables = (variables = []) => {
  const outputResponse = {};
  (variables || []).forEach((v) => {
    outputResponse[isSet(v.api_name, v.apiName)] = v;
  });
  return outputResponse;
};

/** Evaluate pages_message conditions (same logic as To Mapping rules check). */
export const evaluatePagesMessageCondition = (rules = [], outputResponse = {}) => {
  const safeRules = isSet(rules, []);
  let validationResult = "(true===true)";
  if (safeRules.length > 0) {
    validationResult += " && (";
  }
  safeRules.forEach((rule, ruleIndex) => {
    const conditionField = {
      isExist: isSet(outputResponse.pages_message, null) !== null,
      field: {
        type: "TextInput",
        ...outputResponse.pages_message,
        selectedValue: outputResponse.pages_message?.value,
      },
      selectedValue: outputResponse.pages_message?.value,
    };
    if (conditionField.isExist === true) {
      if (ruleIndex > 0) {
        if (isSet(rule.logical_operator, "") === "AND") {
          validationResult += " &&";
        } else if (isSet(rule.logical_operator, "") === "OR") {
          validationResult += " || ";
        }
      }
      validationResult +=
        " (true===" +
        conditionRulesChecking(conditionField, {
          values: rule.value,
          filter: isSet(rule.filter_name, "is not empty"),
        }) +
        " )";
    } else {
      if (ruleIndex > 0) {
        validationResult += " &&";
      }
      validationResult += "(true===false)";
    }
  });
  if (safeRules.length > 0) {
    validationResult += ")";
  }
  try {
    if (eval("(" + validationResult + ")")) {
      return true;
    }
  } catch (e) {
    // ignore eval errors
  }
  return false;
};

/** Build pages-config-execute payload for Lookup / MultiLookup (search, pagination, filterVar). */
export const buildLookupExecutePayload = ({
  fromMapping,
  inputMap,
  config,
  fieldFormPayload,
  search = "",
  page = 0,
  perPage = 10,
}) => {
  const searchField = config?.searchField || "";
  const offsetField = config?.offset || "";
  const limitField = config?.limit || "";
  const filterFields = config?.filterFields || [];

  return fromMapping.map((item) => {
    const apiName = isSet(item?.pages_variable_name, inputMap[item.pages_variable_id]);

    let selectedValue = "";
    if (apiName === searchField) {
      selectedValue = search;
    } else if (apiName === offsetField) {
      selectedValue = page.toString();
    } else if (apiName === limitField) {
      selectedValue = perPage.toString();
    } else if (filterFields.length > 0 && filterFields.includes(apiName)) {
      selectedValue = formatExecuteSelectedValue(
        getFilterPayloadValue(fieldFormPayload, apiName)
      );
    }

    return {
      ...item,
      selected_value: isSet(selectedValue, ""),
    };
  });
};

/** Pages lookup selected value — record_id only (never record.id) */
export const getLookupRecordId = (record, recordIdField) => {
  if (record == null || typeof record !== "object" || Array.isArray(record)) {
    return "";
  }

  let field =
    typeof recordIdField === "string" ? recordIdField.trim() : "";
  if (!field || field.toLowerCase() === "id") {
    field = LOOKUP_RECORD_ID_FIELD;
  }

  let raw;
  if (field.includes(".")) {
    const keys = field.split(".").filter(Boolean);
    if (!keys.length) return "";
    raw = keys.reduce((obj, key) => {
      if (obj == null || typeof obj !== "object") return undefined;
      return obj[key];
    }, record);
  } else {
    raw = record[field];
  }

  if (raw == null || raw === "") return "";
  return String(raw);
};

/** Table row id (record.id) — used as lookup/multilookup `id` */
export const getLookupRowId = (record) => {
  if (record == null || typeof record !== "object" || Array.isArray(record)) {
    return "";
  }
  const raw = record.id;
  return raw != null && raw !== "" ? String(raw) : "";
};

export const buildLookupSelection = (record, recordIdField, label) => {
  const value = getLookupRecordId(record, recordIdField);
  const id = getLookupRowId(record);
  if (!value) return null;
  const labelStr = label != null && label !== "" ? String(label) : "";
  return { id: id || value, label: labelStr, value };
};

export const buildMultiLookupSelection = (record, recordIdField, label) => {
  const item = buildLookupSelection(record, recordIdField, label);
  return item ? { ...item, text: item.label } : null;
};

const hasLookupLabel = (obj) =>
  obj != null && typeof obj === "object" && ("label" in obj || "text" in obj);

const isLookupItem = (item) =>
  item != null && typeof item === "object" && ("value" in item || hasLookupLabel(item));

const coerceToLookupEntry = (obj) => {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return null;
  if (hasLookupLabel(obj) || "value" in obj) return obj;

  // Pages automation/steps sometimes return raw records like {record_id, CompanyName}
  // or {recordId, name}. Convert them into {label, value} so Lookup/MultiLookup can render.
  const rawValue =
    obj.record_id ??
    obj.recordId ??
    obj.recordID ??
    obj.value ??
    obj.id;
  if (rawValue == null || rawValue === "") return null;

  const value = String(rawValue);
  const id = obj.id != null && obj.id !== "" ? String(obj.id) : "";

  // Label is intentionally left empty here because we can't know
  // which field is the "display label" without the lookup config.
  // Components (Lookup/MultiLookup) should use config.defaultField / resultFields for label.
  return { id, value };
};

/** Unwrap pages-variable / nested wrappers; keep arrays and lookup objects intact */
export const normalizeOutputResponseValue = (raw) => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return raw;
  if ("selected_value" in raw) return normalizeOutputResponseValue(raw.selected_value);
  if ("value" in raw) {
    const inner = raw.value;
    if (inner != null && typeof inner === "object") return inner;
    if (hasLookupLabel(raw)) return raw;
    return inner;
  }
  return raw;
};

const resolveLookupEntry = (selected) => {
  if (selected == null) return null;
  if (Array.isArray(selected)) {
    const found = selected.find(isLookupItem);
    if (found) return found;
    const recordLike = selected.find((it) => coerceToLookupEntry(it) != null);
    return recordLike ? coerceToLookupEntry(recordLike) : null;
  }
  if (typeof selected !== "object") {
    const str = String(selected);
    return str ? { label: "", value: str, id: "" } : null;
  }
  if ("selected_value" in selected) return resolveLookupEntry(selected.selected_value);
  if ("value" in selected) {
    const inner = selected.value;
    if (inner != null && typeof inner === "object") return resolveLookupEntry(inner);
    if (hasLookupLabel(selected)) return selected;
    return inner != null && inner !== ""
      ? { label: "", value: String(inner), id: selected.id != null ? String(selected.id) : "" }
      : null;
  }
  if (hasLookupLabel(selected)) return selected;
  return coerceToLookupEntry(selected);
};

export const getLookupStoredValue = (selected) => {
  const entry = resolveLookupEntry(selected);
  if (entry?.value != null && entry.value !== "") return String(entry.value);
  if (typeof selected === "string" || typeof selected === "number") return String(selected);
  return "";
};

export const getLookupStoredLabel = (selected) => {
  const entry = resolveLookupEntry(selected);
  const label = entry?.label ?? entry?.text;
  return label != null && label !== "" ? String(label) : "";
};

/** Stable canonical form for filterVar change detection (string, number, array, object). */
const normalizeFilterValueForCompare = (value) => {
  if (value == null) return "";

  if (typeof value !== "object") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map(normalizeFilterValueForCompare)
      .sort()
      .join("|");
  }

  const unwrapped = normalizeOutputResponseValue(value);
  if (unwrapped !== value) {
    return normalizeFilterValueForCompare(unwrapped);
  }

  const lookupValue = getLookupStoredValue(value);
  if (lookupValue) {
    return lookupValue;
  }

  return Object.keys(value)
    .sort()
    .map((key) => `${key}:${normalizeFilterValueForCompare(value[key])}`)
    .join("|");
};

export const hasFilterPayloadChanged = (prevPayload, nextPayload, filterFields = []) => {
  if (!filterFields.length) return false;

  return filterFields.some((name) => {
    const prevVal = normalizeFilterValueForCompare(getFilterPayloadValue(prevPayload, name));
    const nextVal = normalizeFilterValueForCompare(getFilterPayloadValue(nextPayload, name));
    return prevVal !== nextVal;
  });
};

/**
 * Extract children from Icons tag
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Array|null} - Children of Icons tag or null if not found
 */
export const extractLookupIcon = (children) => {
  if (!children || !Array.isArray(children)) return null;

  // Find Icons tag
  const iconsNode = children.find(
    (node) => {
      if (!(node.type === "tag" && node.name)) return false;
      const nodeName = node.name.toLowerCase();
      // Support both legacy <Icons> and namespaced <scm:icons>
      return nodeName === "icons" || nodeName.endsWith(":icons");
    }
  );

  if (!iconsNode || !iconsNode.children) return null;

  // Return the children of Icons tag
  return iconsNode.children;
};

/**
 * Helper method to extract text content from a node
 * @param {Object} node - Parsed XML/HTML node
 * @returns {String} - Text content from the node
 */
export const getTextContent = (node) => {
  if (!node || !node.children) return "";
  
  return node.children
    .filter(child => child.type === "text")
    .map(child => child.data || "")
    .join("")
    .trim();
};

/**
 * Extract config attributes from config tag (nested structure)
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Object|null} - Extracted config object or null if not found
 */
export const extractConfigAttributes = (children) => {
  if (!children || !Array.isArray(children)) {
    console.log('extractConfigAttributes: children is not an array', children);
    return null;
  }

  console.log('extractConfigAttributes: searching in children', children);

  // Find config tag (case-insensitive)
  const configNode = children.find(
    (node) => {
      const isTag = node.type === "tag";
      const nodeName = (node.name || "").toLowerCase();
      // Support both legacy <Config> and namespaced <scm:config>
      const nameMatches = nodeName === "config" || nodeName.endsWith(":config");
      console.log('Checking node:', { type: node.type, name: node.name, isTag, nameMatches });
      return isTag && nameMatches;
    }
  );

  if (!configNode) {
    console.log('extractConfigAttributes: Config node not found');
    return null;
  }

  console.log('extractConfigAttributes: Found config node', configNode);
  console.log('extractConfigAttributes: configNode.attribs', configNode.attribs);
  console.log('extractConfigAttributes: configNode.children', configNode.children);

  // Extract action from attributes
  const apiAction = configNode.attribs?.action || configNode.attribs?.apiAction || "";

  // Extract from nested tags
  // First, try to read flattened attributes from <scm:config>
  let searchField = configNode.attribs?.searchVar || configNode.attribs?.searchvar || "";
  let offset = configNode.attribs?.offsetVar || configNode.attribs?.offsetvar || "";
  let limit = configNode.attribs?.limitVar || configNode.attribs?.limitvar || "";
  const filterVarRaw =
    configNode.attribs?.filterVar ||
    configNode.attribs?.filtervar ||
    configNode.attribs?.filter ||
    "";
  const filterFields = parseFilterVarFields(filterVarRaw);
  let resultVar = "";
  let recordIdField = "";
  let defaultField = "";
  let totalRecordVar = "";
  const resultFields = [];

  if (configNode.children && Array.isArray(configNode.children)) {
    configNode.children.forEach((child) => {
      if (child.type === "tag") {
        const tagName = (child.name || "").toLowerCase();

        // Extract Search tag
        // (legacy structure: <Config><Search>search_input</Search>...</Config>)
        if (tagName === "search") {
          searchField = getTextContent(child) || "";
        }

        // Extract Pagination tag
        if (tagName === "pagination" && child.children) {
          child.children.forEach((paginationChild) => {
            if (paginationChild.type === "tag") {
              const paginationTagName = (paginationChild.name || "").toLowerCase();
              if (paginationTagName === "offset") {
                offset = getTextContent(paginationChild) || "";
              } else if (paginationTagName === "limit") {
                limit = getTextContent(paginationChild) || "";
              }
            }
          });
        }

        // Extract Result tag
        // Support both legacy <Result> and namespaced <scm:result>
        if (tagName === "result" || tagName.endsWith(":result")) {
          resultVar = child.attribs?.var || "";
          recordIdField = child.attribs?.recordId || child.attribs?.recordid || "";
          defaultField = child.attribs?.defaultField || child.attribs?.defaultfield || "";
          totalRecordVar = child.attribs?.totalRecord || child.attribs?.totalrecord || "";
          // Extract Field tags from Result - recursively find all Field tags
          console.log('Result tag found, children:', child.children);
          console.log('Result tag children length:', child.children?.length);
          
          // Helper function to recursively find all Field tags
          const extractFieldTags = (node) => {
            if (!node || !node.children || !Array.isArray(node.children)) {
              return;
            }
            
            node.children.forEach((fieldChild, index) => {
              console.log(`Processing child ${index}:`, { 
                type: fieldChild.type, 
                name: fieldChild.name, 
                attribs: fieldChild.attribs,
                hasChildren: !!fieldChild.children
              });
              
              // Check if this is a Field tag
              const fieldName = (fieldChild.name || "").toLowerCase();
              // Support both legacy <Field> and namespaced <scm:field>
              if (fieldChild.type === "tag" && fieldChild.name && (fieldName === "field" || fieldName.endsWith(":field"))) {
                const fieldData = {
                  key: fieldChild.attribs?.key || fieldChild.attribs?.Key || "",
                  label: fieldChild.attribs?.label || fieldChild.attribs?.Label || "",
                };
                console.log('Adding field to resultFields:', fieldData);
                resultFields.push(fieldData);
              }
              
              // Recursively check children (in case Field tags are nested)
              if (fieldChild.children && Array.isArray(fieldChild.children)) {
                extractFieldTags(fieldChild);
              }
            });
          };
          
          if (child.children && Array.isArray(child.children)) {
            extractFieldTags(child);
          }
          
          console.log('Total resultFields extracted:', resultFields.length, resultFields);
        }
      }
    });
    // console.log('resultFields1111',resultFields);
  }

  const extractedConfig = {
    apiAction,
    searchField,
    offset,
    limit,
    filterVar: filterVarRaw,
    filterFields,
    resultVar,
    defaultField,
    recordIdField,
    totalRecordVar,
    resultFields,
  };

  console.log('extractConfigAttributes: Extracted config', extractedConfig);
  return extractedConfig;
};

/**
 * Convert node children to HTML string
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {String} - HTML string representation of the nodes
 */
export const renderNodeChildren = (children) => {
  if (!children || !Array.isArray(children)) return "";

  return children
    .map((child) => {
      if (child.type === "text") {
        return child.data || "";
      } else if (child.type === "tag") {
        const attributes = Object.entries(child.attribs || {})
          .map(([key, value]) => `${key}="${value}"`)
          .join(" ");

        const childContent = renderNodeChildren(child.children || []);
        return `<${child.name}${attributes ? ` ${attributes}` : ""}>${childContent}</${child.name}>`;
      }
      return "";
    })
    .join("");
};

